import { Fragment } from "react";

import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

/**
 * Shape used by every invoice-to-client backend that returns an email
 * preview alongside the WhatsApp message (follow-up, opposition counter,
 * etc.). Kept loose so individual API modules can re-export it via their
 * own typed alias without coupling to a single feature module.
 */
export interface InvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

/**
 * Parses the backend-rendered WhatsApp message into a header (first non-empty
 * line) and `Label : Value` rows. Handles two backend shapes:
 *  - inline:    "Label : Value"
 *  - two-line:  "Label:" followed by the value on the next non-empty line
 *               (the follow-up payment template uses this shape).
 * Lines without a `:` and not consumed as a value fall through as free-form text.
 */
export function parsePreviewMessage(message: string): {
  header: string;
  rows: Array<{ label: string; value: string } | { text: string }>;
} {
  const lines = message.split("\n");
  let header = "";
  const rows: Array<{ label: string; value: string } | { text: string }> = [];

  let i = 0;
  for (; i < lines.length; i++) {
    if (lines[i].trim()) {
      header = lines[i].trim();
      i++;
      break;
    }
  }

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }
    const idx = line.indexOf(":");
    if (idx > 0) {
      const label = line.slice(0, idx).trim();
      const inlineValue = line.slice(idx + 1).trim();
      if (inlineValue) {
        rows.push({ label, value: inlineValue });
        i++;
        continue;
      }
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) {
        rows.push({ label, value: lines[j].trim() });
        i = j + 1;
        continue;
      }
      rows.push({ label, value: "" });
      i++;
      continue;
    }
    rows.push({ text: line });
    i++;
  }

  return { header, rows };
}

/**
 * Renders a parsed WhatsApp message: bold header line + tight `Label : Value`
 * rows. Used inside both the Preview modal tab and the Confirm-send modal so
 * the user always sees the exact same format that gets sent.
 */
export function WhatsAppMessageBlock({
  message,
  loading,
  emptyText,
}: {
  message: string;
  loading?: boolean;
  emptyText?: string;
}) {
  if (loading) {
    return (
      <p className="text-sm text-[#6d6d6d]" style={{ fontFamily: "Inter, sans-serif" }}>
        Loading message preview…
      </p>
    );
  }
  if (!message) {
    return (
      <p
        className="text-sm text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {emptyText ?? "Message preview not available."}
      </p>
    );
  }
  const { header, rows } = parsePreviewMessage(message);
  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1.5 font-sans text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {header && (
        <p className="col-span-3 m-0 mb-2 break-words font-medium">
          {renderWhatsAppFormatting(header)}
        </p>
      )}
      {rows.map((row, i) =>
        "label" in row ? (
          <Fragment key={i}>
            <div className="break-words text-[#222]">
              {renderWhatsAppFormatting(row.label)}
            </div>
            <div className="text-[#222]">:</div>
            <div className="min-w-0 break-words text-[#222]">
              {renderWhatsAppFormatting(row.value)}
            </div>
          </Fragment>
        ) : (
          <p key={i} className="col-span-3 m-0 break-words">
            {renderWhatsAppFormatting(row.text)}
          </p>
        )
      )}
    </div>
  );
}

/**
 * Renders the email preview block (To, Subject, Body, optional PDF attachment
 * link). Accepts an `attachmentFallback` because some backend flows transiently
 * null the invoice document key during save, so a recently-generated PDF in
 * client memory can be a more accurate attachment reference.
 */
export function EmailPreviewBlock({
  preview,
  loading,
  attachmentFallback,
}: {
  preview: InvoiceEmailPreview | null;
  loading?: boolean;
  attachmentFallback?: { filename: string; url: string } | null;
}) {
  if (loading) {
    return (
      <p
        className="text-sm text-[#6d6d6d]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Loading email preview…
      </p>
    );
  }
  if (!preview) {
    return (
      <p
        className="text-sm text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Email preview not available.
      </p>
    );
  }
  if (!preview.to) {
    return (
      <p
        className="text-sm text-[#D92D20]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        No email on file for this client. Email will not be sent.
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-1.5 font-sans text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">To</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-all">{preview.to}</span>
      </div>
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">Subject</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-words">{preview.subject}</span>
      </div>
      <div className="mt-2 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] p-3">
        <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
          Body
        </p>
        <p className="m-0 whitespace-pre-wrap break-words text-[#222]">
          {preview.body}
        </p>
      </div>
      {(() => {
        const filename = preview.filename ?? attachmentFallback?.filename ?? null;
        const downloadUrl =
          preview.downloadUrl ?? attachmentFallback?.url ?? null;
        if (!filename) return null;
        return (
          <div className="mt-2 flex flex-row items-baseline gap-2 break-words">
            <span className="text-[#222]">Attachment</span>
            <span className="text-[#222]">:</span>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2E3037] underline underline-offset-2 break-all"
              >
                {filename}
              </a>
            ) : (
              <span className="text-[#222] break-all">{filename}</span>
            )}
          </div>
        );
      })()}
    </div>
  );
}
